(() => {
  if (window.QuadroRuntime) return;
  const config = document.currentScript;
  const pending = new Map();
  const failures = new Set();
  const submitting = new WeakMap();
  const composing = new WeakSet();
  const source = name => config?.getAttribute(`data-${name}-src`);
  const focusFormStatus = () => {
    if (window.QuadroEUPages) return;
    const status = document.querySelector('[data-form-status]');
    if (!status || !status.getClientRects().length) return;
    if (!status.matches('input,select,button,a,textarea')) status.tabIndex = -1;
    status.focus();
  };
  if (document.readyState === 'complete') focusFormStatus();
  else document.addEventListener('DOMContentLoaded', focusFormStatus, { once: true });
  const requiresShipping = () => !!document.querySelector('[data-quadro-cart-shipping]');
  const loaded = name => ({ cart: window.QuadroCart && (!requiresShipping() || window.QuadroCartShipping), shipping: window.QuadroCartShipping, search: window.QuadroPredictiveSearch, lightbox: window.quadroImageLightbox, community: window.QuadroCommunitySlideshow })[name];
  let cartStyle;
  const cartStyles = () => {
    const url = config?.getAttribute('data-cart-css');
    if (!url) return Promise.resolve();
    if (cartStyle) return cartStyle;
    cartStyle = new Promise((resolve, reject) => {
      const href = new URL(url, document.baseURI).href;
      const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.href === href);
      if (existing?.sheet) { resolve(); return; }
      const link = existing || document.createElement('link');
      const timer = setTimeout(() => { failures.add('cart'); reject(new Error('Cart stylesheet unavailable')); }, 10000);
      link.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
      link.addEventListener('error', () => { clearTimeout(timer); failures.add('cart'); reject(new Error('Cart stylesheet unavailable')); }, { once: true });
      if (!existing) { link.rel = 'stylesheet'; link.href = href; document.head.append(link); }
    });
    return cartStyle;
  };
  const load = (name, url = source(name)) => {
    if (loaded(name)) return Promise.resolve();
    if (pending.has(name)) return pending.get(name);
    if (!url) return Promise.reject(new Error('Module unavailable'));
    const promise = Promise.all([
      name === 'cart' ? cartStyles() : Promise.resolve(),
      name === 'cart' && requiresShipping() ? load('shipping') : Promise.resolve()
    ]).then(() => new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      if (name === 'lightbox') [...config.attributes].forEach(attribute => {
        if (attribute.name === 'data-lightbox-css' || /^data-(close|previous|next|zoom|error|retry)-label$/.test(attribute.name)) script.setAttribute(attribute.name, attribute.value);
      });
      const failed = () => { clearTimeout(timer); script.remove(); failures.add(name); reject(new Error('Module unavailable')); };
      const timer = setTimeout(failed, 10000);
      script.onload = () => {
        clearTimeout(timer);
        if (['cart', 'shipping', 'search', 'lightbox', 'community'].includes(name) && !loaded(name)) failed();
        else resolve();
      };
      script.onerror = failed;
      document.head.append(script);
    })).catch(error => { failures.add(name); throw error; });
    pending.set(name, promise);
    return promise;
  };
  const isPlainClick = event => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  const cartTrigger = '[data-cart-drawer-open],.js-go-cart-trigger';
  const lightboxTrigger = '[data-product-lightbox-trigger],.fancybox';
  const communityControl = '[data-community-slider] [data-prev],[data-community-slider] [data-next],[data-community-slider] [data-dot],[data-community-slider] [data-toggle]';
  const searchInput = 'form[data-quadro-predictive-search] input[name="q"]';
  const isAddForm = form => {
    if (!(form instanceof HTMLFormElement) || form.hasAttribute('data-quadro-cart-native')) return false;
    const url = new URL(form.action, location.href);
    return url.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
  };
  const busy = (element, active) => {
    if (active) element.setAttribute('aria-busy', 'true');
    else element.removeAttribute('aria-busy');
  };
  const replayClick = element => { if (element.isConnected) element.click(); };
  const nativeSubmit = (form, body) => {
    const fallback = document.createElement('form');
    fallback.method = form.method;
    fallback.action = form.action;
    fallback.enctype = form.enctype;
    fallback.target = form.target;
    fallback.hidden = true;
    for (const [name, value] of body) {
      const input = document.createElement('input');
      input.name = name;
      if (value instanceof File) {
        input.type = 'file';
        if (value.name || value.size) {
          const transfer = new DataTransfer();
          transfer.items.add(value);
          input.files = transfer.files;
        }
        fallback.enctype = 'multipart/form-data';
      } else {
        input.type = 'hidden';
        input.value = value;
      }
      fallback.append(input);
    }
    document.body.append(fallback);
    HTMLFormElement.prototype.submit.call(fallback);
    fallback.remove();
  };
  const canOpenImage = trigger => {
    if (!window.HTMLDialogElement || typeof HTMLDialogElement.prototype.showModal !== 'function') return false;
    const image = trigger.querySelector('img');
    const value = trigger.getAttribute('data-lightbox-src') || trigger.getAttribute('data-src') || trigger.getAttribute('href') || image?.currentSrc || image?.src;
    try {
      const url = new URL(value, document.baseURI);
      return !!value && /^https?:$/.test(url.protocol) && !/\.(?:mp4|webm|mov|m3u8|glb|gltf|pdf)(?:$|[?#])/i.test(url.href) && !/(^|\.)(youtube\.com|youtu\.be|vimeo\.com)$/i.test(url.hostname) && (image || trigger.hasAttribute('data-product-lightbox-trigger') || /\.(?:avif|gif|jpe?g|png|svg|webp)(?:$|[?#])/i.test(url.href));
    } catch { return false; }
  };
  const startSearch = input => {
    if (failures.has('search') || loaded('search') || input.closest('form')?.dataset.predictiveSearchEnabled === 'false') return;
    load('search').then(() => {
      if (input.isConnected && document.activeElement === input && input.value.trim() && !composing.has(input)) input.dispatchEvent(new Event('input', { bubbles: true }));
    }).catch(() => {});
  };
  document.addEventListener('compositionstart', event => { if (event.target.matches(searchInput)) composing.add(event.target); }, true);
  document.addEventListener('compositionend', event => { if (event.target.matches(searchInput)) { composing.delete(event.target); startSearch(event.target); } }, true);
  document.addEventListener('input', event => { if (event.target.matches(searchInput)) startSearch(event.target); }, true);
  const intent = event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches(searchInput)) startSearch(event.target);
    if (!loaded('cart') && !failures.has('cart') && (event.target.closest(cartTrigger) || isAddForm(event.target.closest('form')))) load('cart').catch(() => {});
    if (source('lightbox') && event.target.closest(lightboxTrigger) && !failures.has('lightbox')) load('lightbox').catch(() => {});
    if (source('community') && event.target.closest('[data-community-slider]') && !failures.has('community')) load('community').catch(() => {});
  };
  document.addEventListener('pointerover', intent, { passive: true });
  document.addEventListener('pointerdown', intent, { passive: true });
  document.addEventListener('focusin', intent);
  document.addEventListener('click', event => {
    if (!isPlainClick(event) || event.defaultPrevented || !(event.target instanceof Element)) return;
    const cart = event.target.closest(cartTrigger);
    const image = event.target.closest(lightboxTrigger);
    const community = event.target.closest(communityControl);
    let name, target;
    if (cart) { name = 'cart'; target = cart; }
    else if (image && canOpenImage(image)) { name = 'lightbox'; target = image; }
    else if (community) { name = 'community'; target = community; }
    if (!name || !source(name) || loaded(name) || failures.has(name)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    busy(target, true);
    load(name).then(() => { busy(target, false); replayClick(target); }).catch(() => {
      busy(target, false);
      if (name !== 'community') replayClick(target);
    });
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    const button = event.submitter || form.querySelector?.('[type="submit"],[data-add-to-cart],[data-add-to-cart-button]');
    if (loaded('cart') || failures.has('cart') || !source('cart') || !isAddForm(form) || button?.name === 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button?.disabled || button?.getAttribute('aria-disabled') === 'true' || form.classList.contains('product-form--variant-sold-out') || !form.reportValidity()) return;
    const body = new FormData(form);
    const fingerprint = JSON.stringify(Array.from(body, ([key, value]) => [key, value instanceof File ? [value.name, value.size, value.lastModified] : value]));
    let submissions = submitting.get(form);
    if (!submissions) { submissions = new Set(); submitting.set(form, submissions); }
    if (submissions.has(fingerprint)) return;
    submissions.add(fingerprint);
    busy(form, true);
    const resume = success => {
      submissions.delete(fingerprint);
      if (!submissions.size) submitting.delete(form);
      busy(form, false);
      if (!form.isConnected) return;
      if (success && window.QuadroCart?.add) { window.QuadroCart.add(form, button, body); return; }
      nativeSubmit(form, body);
    };
    load('cart').then(() => resume(true), () => resume(false));
  }, true);
  document.addEventListener('quadro:cart-updated', event => {
    if (loaded('cart') || failures.has('cart') || !event.detail?.cart || event.detail.source === 'quadro-cart') return;
    const detail = event.detail;
    load('cart').then(() => document.dispatchEvent(new CustomEvent('quadro:cart-updated', { detail }))).catch(() => {});
  });
  const observeCommunity = (root = document) => {
    if (!source('community') || loaded('community') || failures.has('community')) return;
    const sliders = [...(root.matches?.('[data-community-slider]') ? [root] : []), ...root.querySelectorAll('[data-community-slider]')];
    if (!sliders.length) return;
    if (!('IntersectionObserver' in window)) { load('community').catch(() => {}); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load('community').catch(() => {}); }
    }, { rootMargin: '600px 0px' });
    sliders.forEach(slider => observer.observe(slider));
  };
  document.addEventListener('shopify:section:load', event => observeCommunity(event.target));
  observeCommunity();
  let pointer = null;
  let suppressUntil = 0;
  document.addEventListener('pointerdown', event => {
    pointer = event.button === 0 && event.target.closest?.('.link_slide,a.slideshow__btn') ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
  }, true);
  document.addEventListener('pointerup', event => {
    if (pointer?.id !== event.pointerId) return;
    if (Math.abs(event.clientX - pointer.x) > 10 || Math.abs(event.clientY - pointer.y) > 10) suppressUntil = Date.now() + 50;
    pointer = null;
  }, true);
  document.addEventListener('pointercancel', () => { pointer = null; }, true);
  document.addEventListener('click', event => {
    if (Date.now() < suppressUntil && event.target.closest?.('.link_slide,a.slideshow__btn')) { event.preventDefault(); event.stopPropagation(); suppressUntil = 0; }
  }, true);
  window.QuadroRuntime = { load };
})();
